/**
 * Compose a broadcast message — ADR 0053. Pick a segment (scope + audience),
 * see a live recipient count, then Review & send. Sending is create (draft)
 * followed by send (202 → the communications queue); a 503 from the send
 * endpoint carries a user-appropriate message that the store surfaces as a
 * toast.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import useOrganizationStore from '@/store/organizationStore';
import useRegistrationStore from '@/store/registrationStore';
import useLeagueStore from '@/store/leagueStore';
import useDivisionStore from '@/store/divisionStore';
import useBroadcastStore, {
  AudiencePreview,
  BroadcastAudience,
  BroadcastScope,
  BroadcastSegment,
} from '@/store/broadcastStore';

import helpers from '@/utils/helpers';
import getRegistrationName from '@staty/shared/utils/getRegistrationName';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { ArrowLeft, Send } from 'lucide-react';

// Same toolbar as the registration Messaging section. Quill 2: 'bullet' is
// NOT a format — listing it logs a registry error.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'indent', 'link'];

/** Quill's "empty" value is markup like <p><br></p> — check the text content. */
const isQuillEmpty = (html: string): boolean => !html.replace(/<[^>]*>/g, '').trim();

const SCOPE_OPTIONS: { value: BroadcastScope; label: string }[] = [
  { value: 'organization', label: 'Everyone in the organization' },
  { value: 'registration', label: 'A registration' },
  { value: 'contest', label: 'A contest' },
  { value: 'division', label: 'A division' },
];

/** Does this entity belong to the active org? Absent owner ⇒ keep (older docs). */
function belongsToOrg(owner: any, orgId: string): boolean {
  if (!owner) return true;
  return helpers.getObjId(owner) === orgId;
}

export default function BroadcastCompose() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { leagueName = '' } = useOutletContext() as { leagueName?: string };
  // organizationStore is untyped (@ts-nocheck) — its hook returns `unknown`.
  const { organizations } = useOrganizationStore() as { organizations?: any[] };

  const organization = organizations?.find((l: any) => l.name === leagueName);
  const orgId: string | undefined = organization?._id;

  const {
    previewAudience: previewAudienceFn,
    createBroadcast: createBroadcastFn,
    sendBroadcast: sendBroadcastFn,
  } = useBroadcastStore((s) => ({
    previewAudience: s.previewAudience,
    createBroadcast: s.createBroadcast,
    sendBroadcast: s.sendBroadcast,
  }));
  const previewAudience = useMemo(() => previewAudienceFn(getToken), [previewAudienceFn, getToken]);
  const createBroadcast = useMemo(() => createBroadcastFn(getToken), [createBroadcastFn, getToken]);
  const sendBroadcast = useMemo(() => sendBroadcastFn(getToken), [sendBroadcastFn, getToken]);

  // Target lists — reuse the exact fetches the Registration/Competitions/Division
  // surfaces use (all keyed by the org name from the outlet context).
  const { registrations, getAllRegistrations } = useRegistrationStore((s: any) => ({
    registrations: s.registrations,
    getAllRegistrations: s.getAllRegistrations,
  }));
  const { leagues, getLeaguesByOrganizationName } = useLeagueStore((s: any) => ({
    leagues: s.leagues,
    getLeaguesByOrganizationName: s.getLeaguesByOrganizationName,
  }));
  const { divisions, getDivisionsByLeagueName } = useDivisionStore((s) => ({
    divisions: s.divisions,
    getDivisionsByLeagueName: s.getDivisionsByLeagueName,
  }));

  useEffect(() => {
    if (!leagueName) return;
    getAllRegistrations(leagueName);
    getLeaguesByOrganizationName(leagueName);
    getDivisionsByLeagueName(leagueName);
  }, [leagueName]);

  const [scope, setScope] = useState<BroadcastScope>('organization');
  const [targetId, setTargetId] = useState('');
  const [audience, setAudience] = useState<BroadcastAudience>('managers');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // The league/division stores union across orgs — scope the option lists.
  const targetOptions: { value: string; label: string }[] = useMemo(() => {
    if (scope === 'registration') {
      return (registrations || []).map((r: any) => ({
        value: r._id,
        label: getRegistrationName(r),
      }));
    }
    if (scope === 'contest') {
      return (leagues || [])
        .filter((l: any) => orgId && belongsToOrg(l.organization, orgId))
        .map((l: any) => ({
          value: l._id,
          label: l.contestType === 'season' ? `${l.name} (season)` : l.name,
        }));
    }
    if (scope === 'division') {
      return (divisions || [])
        .filter((d: any) => orgId && belongsToOrg(d.organization || d.league?.organization, orgId))
        .map((d: any) => ({
          value: d._id,
          label: d.league?.name ? `${d.name} (${d.league.name})` : d.name,
        }));
    }
    return [];
  }, [scope, registrations, leagues, divisions, orgId]);

  const segmentReady = scope === 'organization' || !!targetId;
  const segment: BroadcastSegment = useMemo(
    () => ({
      scope,
      ...(scope !== 'organization' && targetId ? { targetId } : {}),
      audience,
    }),
    [scope, targetId, audience],
  );

  // Live recipient count, debounced on segment change.
  useEffect(() => {
    if (!orgId || !segmentReady) {
      setPreview(null);
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      const result = await previewAudience({ organization: orgId, segment });
      if (cancelled) return;
      setPreview(result);
      setPreviewLoading(false);
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orgId, segmentReady, segment, previewAudience]);

  const canSend =
    !!orgId && segmentReady && !!subject.trim() && !isQuillEmpty(body) && !isSending;

  const handleConfirmSend = async () => {
    if (!orgId) return;
    setIsSending(true);
    try {
      const doc = await createBroadcast({
        organization: orgId,
        segment,
        subject: subject.trim(),
        body,
        bodyFormat: 'html',
      });
      await sendBroadcast(doc._id);
      toast.success('Message queued, sending now');
      navigate('/admin/messages');
    } catch {
      // The store already toasted the server's message (e.g. the 503
      // worker-down message) — just close the confirm so the draft is intact.
      setConfirmOpen(false);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <PageContainer>
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/messages')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader
          className="flex-1"
          title="New message"
          description="Email a segment of your members. Nothing is sent until you review and confirm."
          actions={(
            <Button onClick={() => setConfirmOpen(true)} disabled={!canSend}>
              <Send className="h-4 w-4 mr-1" />
              Review &amp; send
            </Button>
          )}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Audience</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Send to</Label>
              <Select
                value={scope}
                onValueChange={(value) => {
                  setScope(value as BroadcastScope);
                  setTargetId('');
                }}
              >
                <SelectTrigger data-testid="scope-select" aria-label="Send to">
                  <SelectValue placeholder="Choose an audience" />
                </SelectTrigger>
                <SelectContent>
                  {SCOPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {scope !== 'organization' && (
              <div>
                <Label>
                  {scope === 'registration' ? 'Registration' : scope === 'contest' ? 'Contest' : 'Division'}
                </Label>
                <Select value={targetId} onValueChange={setTargetId}>
                  <SelectTrigger data-testid="target-select" aria-label="Target">
                    <SelectValue
                      placeholder={`Choose a ${scope === 'registration' ? 'registration' : scope}`}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {targetOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">Nothing found</div>
                    ) : (
                      targetOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <Label>Who receives it</Label>
            <RadioGroup
              value={audience}
              onValueChange={(value) => setAudience(value as BroadcastAudience)}
              className="mt-2"
            >
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroupItem value="managers" id="audience-managers" />
                Team managers only
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroupItem value="everyone" id="audience-everyone" />
                Everyone (managers + players)
              </label>
            </RadioGroup>
          </div>

          <div className="text-sm" data-testid="audience-preview">
            {!segmentReady ? (
              <span className="text-muted-foreground">Pick a target to see the recipient count.</span>
            ) : previewLoading ? (
              <span className="text-muted-foreground">Counting recipients…</span>
            ) : preview ? (
              <>
                <span className="font-medium">
                  Will send to {preview.recipientCount} recipient{preview.recipientCount === 1 ? '' : 's'}
                </span>
                {preview.sample?.length > 0 && (
                  <span className="text-muted-foreground">
                    {' '}
                    (e.g. {preview.sample.slice(0, 3).join(', ')})
                  </span>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">Recipient count unavailable.</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Schedule change for this weekend"
            />
          </div>
          <div>
            <Label htmlFor="body">Body</Label>
            <div className="quill-editor mt-1" data-testid="body-editor">
              <ReactQuill
                value={body}
                onChange={setBody}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write your message…"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Formatting is preserved in the email. An unsubscribe link is added automatically.
            </p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send this message?</DialogTitle>
            <DialogDescription>
              {preview
                ? `This will email ${preview.recipientCount} recipient${preview.recipientCount === 1 ? '' : 's'}.`
                : 'The recipient count could not be verified. The message will go to the selected audience.'}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm">
            <span className="text-muted-foreground">Subject: </span>
            <span className="font-medium">{subject.trim()}</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={isSending}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSend} disabled={isSending}>
              {isSending ? 'Sending…' : 'Send now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
